import { ComponentProps, ReactNode } from "react";
import { styled } from "~/application/theme";
import { Text } from "~/components/Text";

/**
 * Shared CSS-grid table primitives for the dashboard-bi-v2 tables.
 *
 * Rows use `display: contents` so each cell participates directly in the
 * parent grid. A `display: contents` element cannot paint a background, so
 * zebra striping and hover are applied to the cells themselves:
 *  - zebra: `Row:nth-of-type(even) > *` tints the cells of even rows.
 *  - hover: `Row:hover > *` highlights every cell of the row together, since
 *    `:hover` still resolves on a `display: contents` element via its children.
 *
 * Sticky cells (CostCenterTable first column) carry a solid background so
 * scrolled content never shows through. Odd rows are white by default; the
 * `Row` selectors above override even/hover so the sticky cell always matches
 * its row colour.
 */

// Shared geometry for every cell. Kept as a plain style object (not a
// `styled` component) so header and body cells can each declare their OWN
// `sticky` variant. Extending one `styled` component that already has a
// `sticky` variant made both the base and derived `sticky` rules apply at
// once, and stylesheet source order decided which `backgroundColor` won —
// that is what made the sticky header cell look like a different shade.
const cellBase = {
  display: "flex",
  alignItems: "center",
  px: "$4",
  py: "$3",
  // Grid items default to `min-width: auto`, which prevents them from
  // shrinking below their content — long text would overflow the column.
  // `minWidth: 0` lets the cell stay within its track so text wraps instead.
  minWidth: 0,
} as const;

const numericVariant = {
  numeric: {
    true: { justifyContent: "flex-end" },
    false: { justifyContent: "flex-start" },
  },
} as const;

// Every header cell shares the same background and bottom border so the
// header reads as one uniform strip. The sticky variant only adds the
// sticky position, a higher stacking layer and a subtle right separator —
// the background stays identical to the rest of the header row.
const HeaderCellBase = styled("div", {
  ...cellBase,
  minHeight: "$10",
  borderBottom: "1px solid $neutral-200",
  backgroundColor: "$neutral-50",

  variants: {
    ...numericVariant,
    sticky: {
      true: {
        position: "sticky",
        left: 0,
        // Highest layer WITHIN the table: the header sticky corner sits above
        // the sticky body column and the scrolling header cells. Kept at 1 so
        // it stays below the global app navbar (zIndex 2) and the page AppBar
        // (zIndex 2) — sticky table cells must never cover the page chrome.
        zIndex: 1,
        borderRight: "1px solid $neutral-200",
      },
    },
  },

  defaultVariants: {
    numeric: "false",
  },
});

const BodyCellBase = styled("div", {
  ...cellBase,
  minHeight: "$11",
  borderBottom: "1px solid $neutral-100",

  variants: {
    ...numericVariant,
    sticky: {
      true: {
        position: "sticky",
        left: 0,
        backgroundColor: "$neutrals-white",
        borderRight: "1px solid $neutral-200",
        // Above the scrolling body cells (static, z-index auto) but below the
        // header sticky corner (zIndex 1). A sticky cell with zIndex 0 still
        // creates a stacking context and paints over its static siblings,
        // while staying well below the app navbar / page AppBar (zIndex 2).
        zIndex: 0,
      },
    },
  },

  defaultVariants: {
    numeric: "false",
  },
});

/**
 * Row wrapper. Keeps `display: contents` so cells flow into the grid, while
 * coordinating zebra striping and a unified hover highlight across all cells.
 */
export const TableRow = styled("div", {
  display: "contents",

  "&:nth-of-type(even) > *": {
    backgroundColor: "$neutral-50",
  },

  "&:hover > *": {
    backgroundColor: "$neutral-100",
  },
});

export type TableHeaderCellProps = {
  children: ReactNode;
  numeric?: boolean;
  sticky?: boolean;
};

export const TableHeaderCell = ({ children, numeric, sticky }: TableHeaderCellProps) => (
  <HeaderCellBase numeric={numeric} sticky={sticky}>
    <Text
      size="2"
      fw="600"
      css={{
        color: "$neutrals-dark",
        // Labels wrap onto a second line when the column is too narrow
        // instead of overflowing into the neighbouring header cell.
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </Text>
  </HeaderCellBase>
);

export type TableBodyCellProps = {
  children: ReactNode;
  numeric?: boolean;
  bold?: boolean;
  muted?: boolean;
  sticky?: boolean;
};

export const TableBodyCell = ({ children, numeric, bold, muted, sticky }: TableBodyCellProps) => (
  <BodyCellBase numeric={numeric} sticky={sticky}>
    <Text
      size="3"
      fw={bold ? "600" : "400"}
      css={{
        color: muted ? "$neutrals-base" : "$neutrals-black",
        minWidth: 0,
        width: "100%",
        textAlign: numeric ? "right" : "left",
        // Numeric values stay on one line; text columns (e.g. long
        // cost-center names) wrap within the column instead of overflowing.
        whiteSpace: numeric ? "nowrap" : "normal",
        overflowWrap: "anywhere",
      }}
    >
      {children}
    </Text>
  </BodyCellBase>
);

export type TableRowProps = ComponentProps<typeof TableRow>;
